import { useRouterState } from "@tanstack/react-router"
import { ChevronRight } from "lucide-react"
import { AppRoutes } from "@/shared/lib/routes"
import { Fragment, type Ref, useEffect, useId, useRef, useState } from "react"

import GuaranteeShield from "@/assets/img/compatibility/checkout/guarantee-shield.svg"
import HeroBg from "@/assets/img/compatibility/checkout/hero-bg.svg"
import HeroImage from "@/assets/img/compatibility/checkout/hero.png"
import Lock from "@/assets/img/compatibility/checkout/lock.svg"
import Offer2Pair from "@/assets/img/compatibility/checkout/offer2-pair.png"
import PayApplePay from "@/assets/img/compatibility/checkout/pay-applepay.svg"
import PayMaestro from "@/assets/img/compatibility/checkout/pay-maestro.svg"
import PayMastercard from "@/assets/img/compatibility/checkout/pay-mastercard.svg"
import PayVisa from "@/assets/img/compatibility/checkout/pay-visa.svg"
import ShieldPrivacy from "@/assets/img/compatibility/checkout/shield-privacy.svg"
import StarIcon from "@/assets/img/compatibility/checkout/star.svg"
import VerifiedIcon from "@/assets/img/checkout/verified.svg"
import AvatarEmma from "@/assets/img/compatibility/checkout/avatar-emma.png"
import AvatarOlivia from "@/assets/img/compatibility/checkout/avatar-olivia.png"
import AvatarSophia from "@/assets/img/compatibility/checkout/avatar-sophia.png"
import Feat1 from "@/assets/img/compatibility/checkout/feat-1.png"
import Feat2 from "@/assets/img/compatibility/checkout/feat-2.png"
import Feat3 from "@/assets/img/compatibility/checkout/feat-3.png"
import Feat4 from "@/assets/img/compatibility/checkout/feat-4.png"
import Feat5 from "@/assets/img/compatibility/checkout/feat-5.png"
import type { ZodiacSign } from "@/features/advisors"
import { ANALYTICS_EVENTS, trackEvent } from "@/features/analytics"
import { useCompatibilityData } from "@/features/compatibility/hooks/use-compatibility-data"
import { usePlanSelection } from "@/features/products"
import type { ProductPlan } from "@/features/products/types"
import { SIGN_GLYPH, SIGN_LABEL, signFromDate } from "@/features/quiz"
import { formatMoney } from "@/shared/lib/money"
import { LoadingSpinner } from "@/shared/components/loading-spinner"
import { MobileLayout } from "@/shared/components/mobile-layout"
import { LegalLink } from "@/shared/components/legal-link"
import { useNavigateWithParams } from "@/shared/hooks/use-navigate-with-params"
import { cn } from "@/lib/utils"
import { usePurchaseStore } from "@/stores/purchase-store"
import { useQuizStore } from "@/stores/quiz-store"
import { savingsPercent, strikePrice } from "../domain/pricing"
import { buildConsentDisclaimer } from "../domain/disclaimer"
import { isApplePayAvailable, useCheckout } from "../hooks/use-checkout"
import { hasAdditionalOffers } from "@/features/offers"
import { isValidEmail } from "@/shared/lib/email"
import { ApplePaySheet } from "./apple-pay-sheet"
import { CardPaymentSheet } from "./card-payment-sheet"
import { MoneyBackGuarantee } from "./money-back-guarantee"
import { PaymentButtons } from "./payment-buttons"

type OpenSheet = "card" | "applepay" | null

const FEATURES = [
  { icon: Feat1, title: "Compatibility breakdown", text: "See your overall match and key areas of connection." },
  { icon: Feat2, title: "Emotional connection insights", text: "Understand your emotional bond, chemistry, and attraction." },
  { icon: Feat3, title: "Trust & honesty analysis", text: "Discover how trust, loyalty, and communication flow between you two." },
  { icon: Feat4, title: "Relationship challenges", text: "Reveal potential conflicts, weak spots, and how to overcome them." },
  { icon: Feat5, title: "Future outlook & timing", text: "Get clues about your long-term potential and important relationship milestones." },
]

/** Map a 0–100 compatibility score to the design's rating badge (label + colours). */
function ratingFor(value: number): { label: string; text: string; bg: string } {
  if (value >= 90) return { label: "Excellent", text: "text-cyan", bg: "bg-cyan/20" }
  if (value >= 80) return { label: "Strong", text: "text-gold", bg: "bg-gold/20" }
  if (value >= 65) return { label: "Good", text: "text-green", bg: "bg-green/20" }
  return { label: "Fair", text: "text-peach", bg: "bg-peach/20" }
}

/** "Total due today: ——— $6.99 › $1.00 / N% SAVED" row, from live plan data. */
function PriceRow({ plan }: { plan: ProductPlan }) {
  const strike = strikePrice(plan)
  const savings = savingsPercent(plan)
  return (
    <div className="flex items-center gap-4">
      <span className="text-base font-semibold text-purple-light">Total due today:</span>
      <div className="h-px flex-1 bg-purple-light/20" />
      <div className="flex flex-col items-end gap-1">
        <div className="flex items-center gap-1">
          {strike != null && (
            <span className="text-base text-purple-light/40 line-through decoration-peach">
              {formatMoney(strike, plan.currency)}
            </span>
          )}
          <ChevronRight className="size-3 text-purple-light/40" />
          <span className="text-base font-semibold text-white">
            {formatMoney(plan.price, plan.currency)}
          </span>
        </div>
        {savings > 0 && (
          <span className="rounded-full bg-green/20 px-2 py-0.5 text-[10px] font-semibold text-green">
            {savings}% SAVED
          </span>
        )}
      </div>
    </div>
  )
}

const REVIEWS = [
  { avatar: AvatarSophia, name: "Sophia Mitchell", text: "I was surprised by how accurate the compatibility reading felt. The emotional connection section described our relationship dynamics almost perfectly." },
  { avatar: AvatarOlivia, name: "Olivia Reynolds", text: "The trust and communication insights really stood out. It highlighted both our strengths and the challenges we've actually experienced together." },
  { avatar: AvatarEmma, name: "Emma Carter", text: "I expected something generic, but the report felt personal and detailed. The future outlook gave me a new perspective on our relationship." },
]

/** mm:sec countdown shown in the navbar — two boxed digits with a dotted divider. */
function Countdown({ seconds }: { seconds: number }) {
  const mm = String(Math.floor(seconds / 60)).padStart(2, "0")
  const ss = String(seconds % 60).padStart(2, "0")
  const box = (value: string, label: string) => (
    <div className="flex h-10 w-12 flex-col items-center justify-center rounded-lg bg-purple-light/10">
      <span className="-mb-1 font-display text-xl font-medium text-purple-light tabular-nums">
        {value}
      </span>
      <span className="text-xs text-purple-light/40">{label}</span>
    </div>
  )
  return (
    <div className="flex items-center gap-1">
      {box(mm, "min")}
      <div className="flex flex-col gap-0.5">
        <span className="size-0.5 rounded-full bg-purple-light/40" />
        <span className="size-0.5 rounded-full bg-purple-light/40" />
        <span className="size-0.5 rounded-full bg-purple-light/40" />
      </div>
      {box(ss, "sec")}
    </div>
  )
}

/** A glowing "You + Partner" hero with the heart illustration and overlaid signs. */
function HeroSignPair({ you, partner }: { you: string; partner: string }) {
  const youSign = signFromDate(you)
  const partnerSign = signFromDate(partner)
  const column = (sign: keyof typeof SIGN_LABEL, tag: string, side: "left" | "right") => (
    <div className={cn("absolute top-[17%] h-[70%] w-[31%]", side === "left" ? "left-0" : "right-0")}>
      <div className="-translate-x-1/2 absolute top-[6%] left-1/2 w-[80%] rounded-xl bg-purple-light/10 px-2 py-1 text-center backdrop-blur-md">
        <span className="text-xs text-purple-light/70">{tag}</span>
      </div>
      <img
        src={SIGN_GLYPH[sign]}
        alt=""
        className="-translate-x-1/2 absolute top-[36%] left-1/2 aspect-square w-[45%]"
      />
      <span className="absolute inset-x-0 bottom-0 text-center font-display text-base font-medium text-purple-light">
        {SIGN_LABEL[sign]}
      </span>
    </div>
  )
  return (
    <div className="relative mx-auto aspect-[383/247] w-full max-w-[383px]">
      <img src={HeroImage} alt="" className="pointer-events-none absolute inset-0 size-full object-contain" />
      {column(youSign, "You", "left")}
      {column(partnerSign, "Partner", "right")}
    </div>
  )
}

/** The centreline arc shared by the gauge track and its progress fill (≈226°, r≈30.14). */
const GAUGE_ARC =
  "M8.22491 47.7402C6.70263 44.1372 5.86093 40.1765 5.86093 36.0193C5.86093 19.3737 19.3549 5.87973 36.0005 5.87973C52.6461 5.87973 66.14 19.3737 66.14 36.0193C66.14 40.1765 65.2983 44.1372 63.776 47.7402"

/**
 * The angular rainbow the arc sweeps through, matching Figma's conic fill:
 * starts pink at the bottom-left, ramps red→orange→yellow over the top and only
 * reaches green near the very end of the arc. `from`/`at` place the cone at the
 * arc centre with 0° at the bottom-left start; stops are degrees into the ≈226° sweep.
 */
const GAUGE_CONIC =
  "conic-gradient(from 247deg at 50% 69.4%, #FE537F 0deg, #FF6D4A 40deg, #FF9B00 84deg, #DED500 150deg, #7FE718 235deg, #FE537F 320deg, #FE537F 360deg)"

/**
 * The compatibility ring: a ~226° arc filled to `value`% with the design's
 * pink→red→orange→yellow→green conic rainbow. Replaces the static per-stat chart
 * SVGs so the arc reflects the real score. The conic fill is revealed along the
 * arc by an SVG mask whose dash length equals the percentage.
 */
function CompatibilityGauge({ value }: { value: number }) {
  const id = useId()
  const pct = Math.max(0, Math.min(100, value))
  return (
    <svg
      viewBox="0 0 72 51.907"
      fill="none"
      className="absolute inset-0 size-full"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <mask id={`${id}-mask`}>
          <path
            d={GAUGE_ARC}
            stroke="#fff"
            strokeWidth="6.69767"
            strokeLinecap="round"
            pathLength={100}
            strokeDasharray={`${pct} 100`}
          />
        </mask>
        <linearGradient id={`${id}-deco`} x1="36" y1="-0.018" x2="36" y2="51.05" gradientUnits="userSpaceOnUse">
          <stop stopColor="#DFCBF5" stopOpacity="0.7" />
          <stop offset="1" stopColor="#DFCBF5" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={GAUGE_ARC} stroke="#DFCBF5" strokeOpacity="0.2" strokeWidth="6.69767" strokeLinecap="round" />
      <foreignObject x="0" y="0" width="72" height="51.907" mask={`url(#${id}-mask)`}>
        <div style={{ width: "100%", height: "100%", background: GAUGE_CONIC }} />
      </foreignObject>
      <circle cx="36" cy="35.9819" r="35.7695" stroke={`url(#${id}-deco)`} strokeWidth="0.460935" />
    </svg>
  )
}

function StatCard({ label, score, locked }: {
  label: string
  score?: number
  locked?: boolean
}) {
  const rating = score != null ? ratingFor(score) : undefined
  return (
    <div
      className="relative flex flex-col items-center gap-1.5 overflow-hidden rounded-2xl px-2 pt-2.5"
      style={{
        background:
          "linear-gradient(180.38deg, rgba(223,203,245,0.1) 0.28%, rgba(223,203,245,0) 99.71%)",
      }}
    >
      <span className="whitespace-nowrap text-[10px] font-medium uppercase text-purple-light/40">
        {label}
      </span>
      <div className={cn("flex flex-col items-center", locked && "opacity-0 blur-[6px]")}>
        <div className="relative h-[51.907px] w-[72px] shrink-0">
          <CompatibilityGauge value={score ?? 0} />
          <span className="absolute left-[12.56px] top-[18.59px] w-[53.581px] text-center font-display text-[20.093px] font-medium leading-[normal] text-purple-light">
            {score != null ? `${score}%` : null}
          </span>
        </div>
        {rating && (
          <span
            className={cn(
              "inline-flex min-w-[72px] items-center justify-center whitespace-nowrap rounded-[10.88px] px-[6.7px] py-[5px] text-[8.37px] font-medium uppercase",
              rating.bg,
              rating.text,
            )}
          >
            {rating.label}
          </span>
        )}
      </div>
      {locked && (
        <div className="absolute left-1/2 top-[calc(50%+10px)] flex size-14 -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center gap-0.5">
          <img src={Lock} alt="" className="size-5" />
          <span className="text-sm font-medium text-purple-light">Unlock</span>
        </div>
      )}
    </div>
  )
}

/** The Love Compatibility checkout — the `compatibility` checkout variant (full Figma parity). */
export function CompatibilityCheckout() {
  const navigate = useNavigateWithParams()
  const search = useRouterState({ select: (s) => s.location.search }) as Record<string, unknown>
  const purchaseStatus = usePurchaseStore((s) => s.status)
  const answers = useQuizStore((s) => s.answers)
  const email = (answers.email as string) || ""
  const { selectedPlan, isLoading } = usePlanSelection()
  const { getPair } = useCompatibilityData()

  const [sheet, setSheet] = useState<OpenSheet>(null)
  const [applePayAvailable] = useState(isApplePayAvailable)
  const { payWithApplePay } = useCheckout(selectedPlan ?? undefined, email, { initCard: false })

  // The method-agnostic CTAs (navbar + hero) scroll to the first Apple Pay / card
  // block and centre it, rather than implying a payment method.
  const paymentRef = useRef<HTMLDivElement>(null)
  const scrollToPayment = () =>
    paymentRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
  // Both method-agnostic reveal CTAs (navbar "Get my results" + hero "Reveal my
  // full results") fire Button_Click (Trello GLvba5C9 — the brief asks for both),
  // then scroll to the plans.
  const handleRevealClick = () => {
    trackEvent(ANALYTICS_EVENTS.COMPAT_SUB_SCREEN_BUTTON_CLICK)
    scrollToPayment()
  }
  const [seconds, setSeconds] = useState(9 * 60 + 45)
  // Hide the sticky navbar (timer + "Get my results") once the inline purchase
  // buttons scroll into view, so the CTA isn't duplicated.
  const [paymentOnScreen, setPaymentOnScreen] = useState(false)

  useEffect(() => {
    const id = setInterval(() => setSeconds((s) => (s > 0 ? s - 1 : 0)), 1000)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    const el = paymentRef.current
    if (!el) return
    const io = new IntersectionObserver(([entry]) => setPaymentOnScreen(entry.isIntersecting), {
      rootMargin: "0px 0px -40% 0px",
    })
    io.observe(el)
    return () => io.disconnect()
  }, [isLoading, selectedPlan])

  const nextRoute = hasAdditionalOffers()
    ? AppRoutes.ADDITIONAL_OFFERS
    : typeof search.upsell === "string" && search.upsell
      ? AppRoutes.UPSELL
      : AppRoutes.CREATE_ACCOUNT

  useEffect(() => {
    if (!selectedPlan) return
    trackEvent(ANALYTICS_EVENTS.PURCHASE_PAGE_VIEWED, {
      productId: selectedPlan.id,
      price: selectedPlan.price,
      currency: selectedPlan.currency,
    })
    trackEvent(ANALYTICS_EVENTS.COMPAT_SUB_SCREEN_SHOWN)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlan?.id])

  useEffect(() => {
    if (purchaseStatus === "success") navigate({ to: nextRoute })
  }, [purchaseStatus, navigate, nextRoute])

  const handleSuccess = () => {
    setSheet(null)
    navigate({ to: nextRoute })
  }

  // Skip the email-confirmation sheet when the quiz already captured a valid
  // email — launch Apple Pay straight from the click to keep the user gesture.
  const handleApplePay = async () => {
    trackEvent(ANALYTICS_EVENTS.COMPAT_SUB_SCREEN_SUB_BUTTON_CLICK, {
      metadata: { method: "apple" },
    })
    if (isValidEmail(email)) {
      const ok = await payWithApplePay()
      if (ok) handleSuccess()
    } else {
      setSheet("applepay")
    }
  }

  if (isLoading || !selectedPlan) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-dark-base">
        <LoadingSpinner />
      </div>
    )
  }

  const youDate = typeof answers.dob === "string" ? answers.dob : undefined
  const partnerDate = typeof answers["partner-dob"] === "string" ? answers["partner-dob"] : undefined

  // Real compatibility scores for the user + partner signs (falls back to the
  // design values while the dataset loads or for an unknown pair).
  const youSign = youDate ? signFromDate(youDate) : undefined
  const partnerSign = partnerDate ? signFromDate(partnerDate) : undefined
  const pair =
    youSign && partnerSign
      ? getPair(SIGN_LABEL[youSign] as ZodiacSign, SIGN_LABEL[partnerSign] as ZodiacSign)
      : null
  const emotion = pair?.Emotions.Value ?? 84
  const trust = pair?.Trust.Value ?? 91

  const renderPrice = (ref?: Ref<HTMLDivElement>) => (
    <div className="space-y-4">
      <PriceRow plan={selectedPlan} />
      <div ref={ref}>
        <PaymentButtons
          applePayAvailable={applePayAvailable}
          onApplePay={handleApplePay}
          onCard={() => {
            trackEvent(ANALYTICS_EVENTS.COMPAT_SUB_SCREEN_SUB_BUTTON_CLICK, {
              metadata: { method: "card" },
            })
            setSheet("card")
          }}
        />
      </div>
    </div>
  )

  return (
    <div className="min-h-[100dvh] bg-dark-base font-body text-purple-light">
      {/* Navbar with countdown */}
      <div
        className={cn(
          "sticky top-0 z-20 border-purple-light/10 border-b bg-dark-base/90 backdrop-blur transition-all duration-200",
          paymentOnScreen && "pointer-events-none -translate-y-full opacity-0",
        )}
      >
        <MobileLayout className="flex items-center justify-between py-2">
          <Countdown seconds={seconds} />
          <button
            type="button"
            onClick={handleRevealClick}
            className="flex h-12 w-[180px] items-center justify-center rounded-full bg-gradient-to-b from-peach to-purple-accent font-body text-base font-semibold text-white"
          >
            Get my results
          </button>
        </MobileLayout>
      </div>

      <MobileLayout className="relative isolate flex flex-col pt-10 pb-6">
        {/* Hero general background — purple glow + sparkles */}
        <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[240px] overflow-hidden">
          <div
            className="absolute inset-0"
            style={{
              background: "radial-gradient(120% 90% at 50% -10%, rgba(100,30,220,0.25), transparent 62%)",
            }}
          />
          <img src={HeroBg} alt="" className="absolute inset-x-0 top-0 w-full opacity-80" />
        </div>

        {/* Hero */}
        <div className="mx-auto w-fit rounded-full bg-green/15 px-4 py-1.5 text-center text-xs font-semibold tracking-wide text-green uppercase">
          Exclusive compatibility match is ready
        </div>
        <h1 className="mt-5 text-center font-display text-2xl font-medium text-purple-light">
          Reveal your love match!
        </h1>
        <p className="mt-2 text-center text-base text-purple-light/70">
          Discover how your connection really works — from emotional chemistry and attraction to hidden
          relationship patterns.
        </p>

        <div className="mt-6">
          <HeroSignPair you={youDate ?? ""} partner={partnerDate ?? ""} />
        </div>

        <div className="mt-6 grid grid-cols-4 gap-2">
          <StatCard label="Emotion" score={emotion} />
          <StatCard label="Trust" score={trust} />
          <StatCard label="Love" locked />
          <StatCard label="Potential" locked />
        </div>

        <button
          type="button"
          onClick={handleRevealClick}
          className="mt-6 h-14 w-full rounded-full bg-gradient-to-b from-peach to-purple-accent font-body text-base font-semibold text-white"
        >
          Reveal my full results
        </button>
        <div className="mt-3 flex items-center justify-center gap-2 text-xs text-purple-light/60">
          <img src={ShieldPrivacy} alt="" className="size-4" />
          We do not collect your data
        </div>

        {/* What you unlock */}
        <h2 className="mt-10 text-center font-display text-2xl font-medium text-purple-light">
          See what you can uncover and enjoy inside
        </h2>
        <div className="mt-4 flex flex-col gap-2 rounded-[20px] bg-dark-surface p-4">
          {FEATURES.map((f, i) => (
            <Fragment key={f.title}>
              {i > 0 && <div className="h-px w-full bg-purple-light/10" />}
              <div className="flex items-center gap-2.5">
                <div className="flex size-[52px] shrink-0 items-center justify-center overflow-hidden rounded-[26px] bg-dark-base">
                  <img src={f.icon} alt="" className="size-10 object-contain mix-blend-lighten" />
                </div>
                <div className="min-w-0">
                  <p className="text-base font-semibold text-white">{f.title}</p>
                  <p className="text-sm text-purple-light/70">{f.text}</p>
                </div>
              </div>
            </Fragment>
          ))}
        </div>

        {/* Primary price block */}
        <div className="mt-8">{renderPrice(paymentRef)}</div>
        <div className="mt-4 flex flex-col items-center gap-4 rounded-3xl border border-white/10 p-4">
          <p className="text-sm font-semibold text-white">Safe &amp; Secure Payment</p>
          <div className="flex w-full gap-1">
            {[
              { src: PayVisa, cls: "h-3 w-[37px]" },
              { src: PayMastercard, cls: "h-6 w-[31px]" },
              { src: PayMaestro, cls: "h-6 w-[31px]" },
              { src: PayApplePay, cls: "h-4 w-[38px]" },
            ].map((p) => (
              <div
                key={p.src}
                className="flex h-10 flex-1 items-center justify-center rounded-lg bg-white/10"
              >
                <img src={p.src} alt="" className={p.cls} />
              </div>
            ))}
          </div>
          <div className="h-px w-full bg-white/10" />
          <MoneyBackGuarantee />
          <p className="text-center text-xs text-purple-light/40">
            {buildConsentDisclaimer(selectedPlan)}
          </p>
        </div>

        {/* Testimonials — full-width dark-surface band */}
        <div className="-mx-4 mt-10 flex flex-col gap-6 bg-dark-surface px-4 py-10">
          <h2 className="font-display text-[32px] font-medium leading-tight text-purple-light">
            See What Others Revealed About Their Soulmate
          </h2>
          <div className="flex items-center gap-2">
            <img src={StarIcon} alt="" className="size-8" />
            <span className="font-display text-2xl font-medium">
              <span className="text-gold">4.8</span>
              <span className="text-purple-light"> (2545 readings)</span>
            </span>
          </div>
          <div className="-mr-4 flex gap-4 overflow-x-auto pr-4 pb-2">
            {REVIEWS.map((r) => (
              <div
                key={r.name}
                className="flex w-[300px] shrink-0 flex-col justify-between gap-6 rounded-2xl border border-purple-light/20 p-4"
              >
                <p className="text-base text-purple-light/70">{r.text}</p>
                <div className="flex items-center gap-2">
                  <img src={r.avatar} alt="" className="size-11 rounded-full object-cover" />
                  <div className="flex flex-col gap-1">
                    <span className="font-display text-base font-medium text-purple-light">
                      {r.name}
                    </span>
                    <div className="flex items-center gap-1">
                      <div className="flex">
                        {Array.from({ length: 5 }).map((_, i) => (
                          // biome-ignore lint/suspicious/noArrayIndexKey: fixed 5-star row
                          <img key={i} src={StarIcon} alt="" className="size-4" />
                        ))}
                      </div>
                      <span className="text-sm font-medium text-gold">4.8</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-sm">
            <span className="font-medium text-white">*Disclaimer: </span>
            <span className="text-purple-light/70">
              Every soulmate reading is unique and based on your personal answers and compatibility
              analysis.
            </span>
          </p>
        </div>

        {/* Second offer */}
        <div className="mt-10 flex flex-col items-center gap-4">
          <div className="relative w-full max-w-[382px]">
            <img src={Offer2Pair} alt="" className="w-full" />
            {youSign && (
              <img
                src={SIGN_GLYPH[youSign]}
                alt=""
                className="absolute top-[37%] left-[17.5%] aspect-square w-[14%]"
              />
            )}
            {partnerSign && (
              <img
                src={SIGN_GLYPH[partnerSign]}
                alt=""
                className="absolute top-[37%] left-[68.5%] aspect-square w-[14%]"
              />
            )}
          </div>
          <div className="space-y-2 text-center">
            <h2 className="font-display text-2xl font-medium text-purple-light">
              Reveal your love match
            </h2>
            <p className="text-base text-purple-light/70">
              Unlock your personalized compatibility reading and discover what truly connects you.
            </p>
          </div>
        </div>
        <div className="mt-4 flex flex-col gap-4 rounded-3xl bg-dark-surface p-5">
          {renderPrice()}
          <p className="text-center text-xs text-purple-light/40">{buildConsentDisclaimer(selectedPlan)}</p>
        </div>

        {/* Satisfaction guarantee */}
        <div className="mt-10 flex flex-col items-center gap-4">
          <div className="flex flex-col items-center gap-2 text-center">
            <img src={GuaranteeShield} alt="" className="size-20" />
            <h2 className="font-display text-2xl font-medium text-purple-light">
              Satisfaction Guaranteed
            </h2>
            <p className="text-base text-purple-light/70">
              At Live Palmistry, every sketch, compatibility insight, and personal reading is designed
              to feel personal, meaningful, and worth exploring. If your experience doesn't feel
              magical or insightful, we're here for you.
            </p>
          </div>

          <div className="flex w-full flex-col gap-3 rounded-[20px] bg-purple-light/10 px-4 pt-2 pb-3">
            <div className="text-center">
              <p className="font-display text-2xl font-medium text-purple-light">4.8 out of 5</p>
              <p className="text-sm text-purple-light/70">personal readings revealed worldwide</p>
            </div>
            <div className="h-px w-full bg-purple-light/10" />
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-1">
                  <div className="flex">
                    {Array.from({ length: 5 }).map((_, i) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: fixed 5-star row
                      <img key={i} src={StarIcon} alt="" className="size-4" />
                    ))}
                  </div>
                  <span className="text-sm font-medium text-gold">4.8</span>
                </div>
                <p className="text-xs text-purple-light/40">Based on user experiences</p>
              </div>
              <div className="flex items-center gap-1 rounded-2xl bg-green/10 py-1.5 pr-2 pl-1.5">
                <img src={VerifiedIcon} alt="" className="size-4" />
                <span className="text-xs font-semibold text-green">Verified reviews</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-10 flex flex-col gap-2 text-xs text-purple-light/40">
          <p className="text-center">All rights reserved. 2026</p>
          <div className="h-px w-full bg-purple-light/10" />
          <div className="flex text-center">
            <LegalLink to={AppRoutes.PRIVACY} className="flex-1 hover:text-purple-light">Privacy Policy</LegalLink>
            <LegalLink to={AppRoutes.TERMS} className="flex-1 hover:text-purple-light">Terms of use</LegalLink>
            <LegalLink to={AppRoutes.COOKIES} className="flex-1 hover:text-purple-light">Cookie Policy</LegalLink>
          </div>
        </footer>
      </MobileLayout>

      <CardPaymentSheet
        plan={selectedPlan}
        email={email}
        open={sheet === "card"}
        onClose={() => setSheet(null)}
        onSuccess={handleSuccess}
      />
      <ApplePaySheet
        plan={selectedPlan}
        email={email}
        open={sheet === "applepay"}
        onClose={() => setSheet(null)}
        onSuccess={handleSuccess}
      />
    </div>
  )
}
